import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login, setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for Google OAuth callback (token in URL params)
      const token = searchParams.get("token");
      const userId = searchParams.get("user_id");
      const email = searchParams.get("email");
      const name = searchParams.get("name");
      const role = searchParams.get("role");
      const onboardingCompleted = searchParams.get("onboarding_completed") === "true";

      if (token && userId) {
        // Google OAuth callback - use login() to properly set token in both state and localStorage
        const userData = {
          user_id: userId,
          email: email,
          name: name,
          role: role || "patient",
          onboarding_completed: onboardingCompleted
        };
        await login(userData, token);

        toast.success("Welcome to PlateMind!");

        // Redirect based on role and onboarding status
        if (!onboardingCompleted && role === "patient") {
          navigate("/onboarding");
        } else if (role === "admin") {
          navigate("/admin");
        } else if (role === "dietitian") {
          navigate("/physician");
        } else if (role === "partner_admin") {
          navigate("/partner");
        } else if (role === "corporate_admin") {
          navigate("/corporate");
        } else {
          navigate("/dashboard");
        }
        return;
      }

      // Check for old Emergent auth callback (session_id in hash)
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error("Authentication failed");
        navigate("/login");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          localStorage.setItem("token", sessionId);
          toast.success("Welcome to PlateMind!");
          
          // Check if onboarding is needed
          if (!data.user.onboarding_completed) {
            navigate("/onboarding", { state: { user: data.user } });
          } else {
            navigate("/dashboard", { state: { user: data.user } });
          }
        } else {
          toast.error("Authentication failed");
          navigate("/login");
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        toast.error("Authentication failed");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, login, setUser, searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse flex flex-col items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center">
          <span className="text-white font-bold text-2xl font-heading">M</span>
        </div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
